/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Address } from '@vsolv/core/address/domain';
import { UserService } from '@vsolv/core/users/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { PaymentMethodService } from '../../services';
import { BillingDetailsInputValue } from '../billing-details-input/billing-details-input.component';
import { BankAccountInputValue } from '../bank-account-input/bank-account-input.component';
import { CreditCardInputValue } from '../credit-card-input/credit-card-input.component';

@Component({
  selector: 'vs-payment-method-input',
  templateUrl: './payment-method-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
// Create a new payment method (display billing details form and CC or bank based on "type" input)
export class PaymentMethodInputComponent implements OnDestroy, OnInit {
  constructor(
    private userSvc: UserService,
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef,
    private paymentMethodSvc: PaymentMethodService,
    private toastSvc: ToastService
  ) {}

  @HostBinding('class') _classes = 'block';

  @Output() adding = new EventEmitter<void>();
  @Output() failed = new EventEmitter<void>(true);
  @Output() added = new EventEmitter<{ id: string }>(true);
  @Output() isPaymentMethodInputPage = new EventEmitter<boolean>(false);

  @Input() userId: string | null = null;
  @Input() gatewayInstanceId: string | null = null;
  @Input() userDetails: Pick<NonNullable<BillingDetailsInputValue>, 'name' | 'email' | 'phone'> | null = null;

  @Input() address: Address.Model | null = null;

  private _type: 'credit-card' | 'bank' = 'credit-card';
  @Input() set type(type: 'credit-card' | 'bank') {
    this._type = type;
    this.updateFormStatus();
  }
  get type() {
    return this._type;
  }

  @Input() staffView = false;

  protected errors: string[] = [];

  protected form = this.formBuilder.group({
    billingDetails: [null as BillingDetailsInputValue, [Validators.required]],
    creditCard: [null as CreditCardInputValue, [Validators.required]],
    bankAccount: [null as BankAccountInputValue, [Validators.required]],
  });

  protected processing = false;

  async addCreditCard() {
    if (this.form.invalid) return;

    const { creditCard, billingDetails } = this.form.value;
    this.form.disable();

    this.adding.emit();
    this.processing = true;
    this.changeDetector.markForCheck();

    try {
      if (!creditCard || !billingDetails) throw new Error('Unexpected error');

      const { address, ...details } = billingDetails;
      const result = await this.paymentMethodSvc.addCreditCard({
        cardData: {
          month: creditCard.card.expiration.substring(0, 2),
          year: creditCard.card.expiration.substring(2),
          cardNumber: creditCard.card.number,
          cardCode: creditCard.card.cvc,
          fullName: creditCard.name,
          zip: address.zip,
        },

        billingAddress: { ...address, country: 'USA', line2: address.line2 || null },
        billingDetails: details,

        userId: this.userId ?? undefined,
        gatewayInstanceId: this.gatewayInstanceId ?? undefined,
      });

      this.added.emit(result);
    } catch (err) {
      this.handlePaymentError(err);
    }

    this.processing = false;
    this.changeDetector.markForCheck();

    this.form.enable();
    this.updateFormStatus();
  }

  async addBankAccount() {
    if (this.form.invalid) return;

    const { bankAccount, billingDetails } = this.form.value;
    this.form.disable();

    this.adding.emit();
    this.processing = true;
    this.changeDetector.markForCheck();

    try {
      if (!bankAccount || !billingDetails) throw new Error('Unexpected error');

      const { address, ...details } = billingDetails;
      const result = await this.paymentMethodSvc.addBankAccount({
        routingNumber: bankAccount.routingNumber,
        accountNumber: bankAccount.accountNumber,
        nameOnAccount: bankAccount.nameOnAccount,
        bankName: bankAccount.bankName,
        accountType: bankAccount.accountType,
        billingAddress: { ...address, country: 'USA', line2: address.line2 || null },
        billingDetails: details,
        userId: this.userId ?? undefined,
        gatewayInstanceId: this.gatewayInstanceId ?? undefined,
      });

      this.added.emit(result);
    } catch (err) {
      this.handlePaymentError(err);
    }

    this.processing = false;
    this.changeDetector.markForCheck();

    this.form.enable();
    this.updateFormStatus();
  }

  private handlePaymentError(err: unknown) {
    this.errors = [];

    let code: string | undefined;
    let normalizedMessage: string | undefined;

    let errorObj: any = (err as any)?.error;
    if (typeof errorObj === 'string') {
      try {
        errorObj = JSON.parse(errorObj);
      } catch {
        // Not JSON, leave as string
      }
    }

    const validationErrors = errorObj?.validationErrors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      code = validationErrors[0]?.code;
      normalizedMessage = validationErrors[0]?.message;
    } else if (Array.isArray(errorObj)) {
      const first = errorObj[0];
      code = first?.code;
      normalizedMessage = first?.message;
    } else if (Array.isArray(errorObj?.message) && errorObj.message.length > 0 && errorObj.message[0]?.message) {
      code = errorObj.message[0]?.code;
      normalizedMessage = errorObj.message[0]?.message;
    } else {
      code = errorObj?.code;
      normalizedMessage =
        errorObj?.message ||
        errorObj?.error ||
        (Array.isArray(errorObj?.messages)
          ? errorObj.messages
              .map((m: any) => m?.text)
              .filter(Boolean)
              .join('\n')
          : null) ||
        (typeof (err as any)?.message === 'string' ? (err as any).message : null);
    }

    if (code === 'PAYMENT_PROVIDER_NOT_CONFIGURED') {
      normalizedMessage = 'Payments are not configured for this tenant. Please contact support.';
    }

    if (!normalizedMessage) {
      normalizedMessage = 'An unexpected error occurred.';
    }

    let source: any = errorObj?.source;
    if (!source && Array.isArray(errorObj?.validationErrors) && errorObj.validationErrors.length > 0) {
      source = errorObj.validationErrors[0]?.source;
    }
    if (!source && Array.isArray(errorObj) && errorObj.length > 0) {
      source = errorObj[0]?.source;
    }
    if (!source && Array.isArray(errorObj?.message) && errorObj.message.length > 0) {
      source = errorObj.message[0]?.source;
    }

    const indicator = source === 'authorize.net' ? '[Payment Provider]' : '';

    this.errors = [normalizedMessage];
    this.toastSvc.show({
      type: 'error',
      title: indicator ? `${indicator} ${normalizedMessage}` : normalizedMessage,
    });

    this.failed.emit();
  }

  private updateFormStatus() {
    if (this.type === 'credit-card') {
      this.form.controls.bankAccount.disable();
      this.form.controls.creditCard.enable();
    } else {
      this.form.controls.bankAccount.enable();
      this.form.controls.creditCard.disable();
    }
  }

  async fillUser() {
    if (!this.userDetails) {
      const user = await this.userSvc.getSelf();
      if (user.id === this.userId) {
        this.userDetails = { email: user.email || '', name: user.displayName || '', phone: user.phoneNumber || '' };
      }
    }

    if (this.userDetails) {
      this.form.controls.billingDetails.patchValue({
        name: this.userDetails?.name ?? '',
        email: this.userDetails?.email ?? '',
        phone: this.userDetails?.phone ?? '',
        address: this.form.value.billingDetails?.address ?? {
          line1: this.form.value.billingDetails?.address.line1 || '',
          line2: this.form.value.billingDetails?.address.line2 || '',
          city: this.form.value.billingDetails?.address.city || '',
          state: this.form.value.billingDetails?.address.state || '',
          zip: this.form.value.billingDetails?.address.zip || '',
        },
      });
    }
  }

  async removeUser() {
    this.form.controls.billingDetails.patchValue({
      name: '',
      email: '',
      phone: '',
      address: this.form.value.billingDetails?.address ?? {
        line1: this.form.value.billingDetails?.address.line1 || '',
        line2: this.form.value.billingDetails?.address.line2 || '',
        city: this.form.value.billingDetails?.address.city || '',
        state: this.form.value.billingDetails?.address.state || '',
        zip: this.form.value.billingDetails?.address.zip || '',
      },
    });
  }

  fillShipping() {
    if (this.address) {
      this.form.controls.billingDetails.patchValue({
        name: this.form.value.billingDetails?.name ?? '',
        email: this.form.value.billingDetails?.email ?? '',
        phone: this.form.value.billingDetails?.phone ?? '',
        address: { ...this.address, line2: this.address.line2 || '' },
      });
    }
  }

  removeShippingAddress() {
    this.form.controls.billingDetails.patchValue({
      name: this.form.value.billingDetails?.name ?? '',
      email: this.form.value.billingDetails?.email ?? '',
      phone: this.form.value.billingDetails?.phone ?? '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
    });
  }

  ngOnInit(): void {
    this.isPaymentMethodInputPage.emit(false);
    this.fillUser();
  }

  ngOnDestroy(): void {
    this.isPaymentMethodInputPage.emit(true);
  }
}
